import { useEffect, useState } from "react";

import type { PlotData } from "../../../api";

import {
  apiGetTasksWorkerQuicklookTaskIdJson,
  apiGetTasksWorkerStatusTaskId,
  apiPutTasksWorkerQuicklookHeartcoreMasterPatientId,
  CeleryTaskStates,
  QuickLookReport,
  QuickLookSection,
} from "../../../api";
import { getServerNow } from "../../../utils/server-time";

// 2s poll cadence mirrors the QuickLook model.
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 120_000;

export type HeartcoreVariant = "dri" | "lvfp" | "heartcore";

// How far back the charts ask for — mirrors the endpoint's own accepted set, so
// the two can't drift. The UI offers just two of them: this default and the
// patient's whole history behind "Load all data".
//
// The default is a ceiling, not a floor. A patient with less history simply
// gets a shorter axis, because the plotted domain comes from the points that
// come back rather than from the window requested (see computeChartTimeRange).
export type HeartcoreRange = 3 | 6 | 9 | 12 | "all";
export const DEFAULT_HEARTCORE_RANGE: HeartcoreRange = 6;

// Every tab reads ONE shared HeartCore Master report (see loadMasterReport) —
// only the *primary* metric section differs per tab; heart rate, blood
// pressure, weight, symptoms and SpO2 are the same section for all three.
const PRIMARY_SECTION: Record<HeartcoreVariant, QuickLookSection> = {
  dri: QuickLookSection.dri,
  lvfp: QuickLookSection.lvfp,
  // HeartCore's primary chart is the risk-zone-shaded median line — its own
  // section, distinct from the raw+rolling `lvfp` pair DRI/LVFP read.
  heartcore: QuickLookSection.lvfp_median,
};

// The sections each new-tab report keeps: its old QuickLook button's, plus
// weight (report-parity spec, Coverage). The DRI report also keeps the button's
// LVFP chart. The Trends tabs show every section but that one, unfiltered.
const REPORT_SECTIONS: Record<HeartcoreVariant, QuickLookSection[]> = {
  dri: [
    QuickLookSection.lvfp,
    QuickLookSection.heart_rate,
    QuickLookSection.afib,
    QuickLookSection.blood_pressure,
    QuickLookSection.weight,
    QuickLookSection.symptoms,
    QuickLookSection.spo2,
  ],
  lvfp: [
    QuickLookSection.heart_rate,
    QuickLookSection.blood_pressure,
    QuickLookSection.weight,
    QuickLookSection.symptoms,
    QuickLookSection.spo2,
  ],
  heartcore: [
    QuickLookSection.heart_rate,
    QuickLookSection.blood_pressure,
    QuickLookSection.weight,
    QuickLookSection.symptoms,
  ],
};

const VARIANT_LABEL: Record<HeartcoreVariant, string> = {
  dri: "DRI",
  lvfp: "LVFP",
  heartcore: "HeartCore",
};

// Report-promise cache per patient: every tab shares ONE HeartCore Master
// report (a single submit+poll+fetch chain covers all three), so this is keyed
// by patientId alone — the old per-variant reports each had their own key.
// Radix unmounts inactive tabs, so without a cache each tab switch would
// re-submit; the TTL lets a re-mount eventually pick up new measurements.
const REPORT_TTL_MS = 30 * 60 * 1000;

type CacheEntry = {
  promise: Promise<QuickLookReport>;
  // The resolved report, once it lands. Kept beside the promise so a remount
  // can read it synchronously — awaiting even an already-settled promise costs
  // a render, which is a "Loading charts…" flash on every tab switch.
  report: QuickLookReport | null;
  fetchedAt: number; // performance.now(), drives the TTL
  fetchedAtMs: number; // Date.now(), shown as "last refreshed"
};
// Keyed by patient AND range: "Load all data" is a second, wider report for the
// same patient, and switching back to the default year must not refetch what is
// already in memory.
const reportCache = new Map<string, CacheEntry>();

function cacheKey(patientId: number, range: HeartcoreRange): string {
  return `${patientId}:${range}`;
}

// The report if it's already in memory and still fresh. Null means "not loaded
// yet" — the caller has to go through loadMasterReport.
function cachedReport(
  patientId: number,
  range: HeartcoreRange,
): QuickLookReport | null {
  const entry = reportCache.get(cacheKey(patientId, range));
  if (!entry || performance.now() - entry.fetchedAt >= REPORT_TTL_MS)
    return null;
  return entry.report;
}

// Wall-clock time of the newest report fetch for a patient — the "last
// refreshed" label next to the Reload button. Null before any fetch.
export function latestHeartcoreReportFetch(
  patientId: number,
  range: HeartcoreRange,
): number | null {
  return reportCache.get(cacheKey(patientId, range))?.fetchedAtMs ?? null;
}

// "Reload data" pairs this with a reloadNonce bump so mounted hooks refetch —
// TTL expiry alone only takes effect on the next mount. Every range is dropped,
// not just the one on screen: the button means "this patient's data is stale".
export function clearHeartcoreReports(patientId: number): void {
  for (const key of reportCache.keys()) {
    if (key.startsWith(`${patientId}:`)) reportCache.delete(key);
  }
}

// Warm the cache on profile open and after "Reload data" — without it the
// first tab visited waits for its own submit+poll. Fire-and-forget:
// loadMasterReport handles rejections, and the tab mount surfaces the error
// via the hook.
export function prefetchHeartcoreReports(
  patientId: number,
  range: HeartcoreRange,
): void {
  void loadMasterReport(patientId, range);
}

function loadMasterReport(
  patientId: number,
  range: HeartcoreRange,
): Promise<QuickLookReport> {
  const key = cacheKey(patientId, range);
  const cached = reportCache.get(key);
  if (cached && performance.now() - cached.fetchedAt < REPORT_TTL_MS) {
    return cached.promise;
  }

  const promise = (async () => {
    const submit = await apiPutTasksWorkerQuicklookHeartcoreMasterPatientId({
      path: { patient_id: patientId },
      // Tables stay at 3 months on both settings — nothing here renders them,
      // so widening them would only cost the worker time.
      query: { plot_range_months: range, table_range_months: 3 },
    });
    await pollUntilSuccess(submit.data.task_id);
    const response = await apiGetTasksWorkerQuicklookTaskIdJson({
      path: { task_id: submit.data.task_id },
    });
    return response.data;
  })();

  // On success record the report on the entry, so later mounts can read it
  // without awaiting; on failure drop the entry entirely, so the next mount
  // retries rather than replaying the error. Both first check the entry still
  // belongs to this promise — a "Reload data" mid-flight replaces it, and the
  // superseded attempt must not write into its successor.
  void promise.then(
    (report) => {
      const entry = reportCache.get(key);
      if (entry?.promise === promise) entry.report = report;
    },
    () => {
      if (reportCache.get(key)?.promise === promise) reportCache.delete(key);
    },
  );
  reportCache.set(key, {
    promise,
    report: null,
    fetchedAt: performance.now(),
    fetchedAtMs: getServerNow().getTime(),
  });
  return promise;
}

async function pollUntilSuccess(taskId: string): Promise<void> {
  const deadline = performance.now() + POLL_TIMEOUT_MS;
  while (performance.now() < deadline) {
    const response = await apiGetTasksWorkerStatusTaskId({
      path: { task_id: taskId },
    });
    const { state } = response.data;
    if (state === CeleryTaskStates.success) return;
    if (state === CeleryTaskStates.failure) {
      throw new Error(`QuickLook task ${taskId} failed`);
    }
    await delay(POLL_INTERVAL_MS);
  }
  throw new Error(`QuickLook task ${taskId} timed out`);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// The generated type promises `sections` is an array; the task pipeline
// doesn't. The guard keeps a malformed result on the "no data" path. Matched
// by `section_key`, not `section_name` — LVFP's "lvfp" and HeartCore's
// "lvfp_median" sections share the exact same display name.
//
// A section may carry more than one plot, and every timeseries plot in one
// belongs to the same chart, so their traces are merged. Taking `plots[0]` and
// dropping the rest loses whole lines without a trace of it in the UI: a DRI
// section split into a raw plot and a rolling plot would render the grey raw
// line alone, with the rolling median — and so its arrowheads, error bars and
// decline triangles — silently absent.
function findSectionPlot(
  report: QuickLookReport | null,
  key: QuickLookSection,
): PlotData | null {
  if (!Array.isArray(report?.sections)) return null;
  const plots = report.sections.find((s) => s.section_key === key)?.plots ?? [];
  // Plots without traces (a table-only or histogram plot) contribute nothing to
  // merge, but must still stand in when they're all a section has.
  const withTraces = plots.filter(
    (p) => (p.timeseries?.traces.length ?? 0) > 0,
  );
  if (withTraces.length <= 1) return withTraces[0] ?? plots[0] ?? null;

  const base = withTraces[0];
  if (!base.timeseries) return base;
  return {
    ...base,
    timeseries: {
      ...base.timeseries,
      traces: withTraces.flatMap((p) => p.timeseries?.traces ?? []),
    },
    thresholds: withTraces.flatMap((p) => p.thresholds ?? []),
    reference_lines: withTraces.flatMap((p) => p.reference_lines ?? []),
    // Null only when no plot has them, which tells an older response apart.
    line_stats: withTraces.some((p) => p.line_stats)
      ? withTraces.flatMap((p) => p.line_stats ?? [])
      : null,
    // Rides on whichever plot carries it (see afibSourceSeries).
    afib_probability:
      withTraces.find((p) => p.afib_probability?.length)?.afib_probability ??
      null,
    // Per chart, so likewise taken from whichever plot carries it.
    last_value_threshold_gt:
      withTraces.find((p) => p.last_value_threshold_gt != null)
        ?.last_value_threshold_gt ?? null,
    delta_color_rule:
      withTraces.find((p) => p.delta_color_rule)?.delta_color_rule ?? null,
  };
}

// `reloadNonce` re-runs the fetch when bumped by the owner (the "Reload data"
// button, after clearHeartcoreReports dropped the cache entry). `asReport`
// keeps only the new-tab report's sections (REPORT_SECTIONS).
export function useHeartcoreChartData(
  patientId: number,
  variant: HeartcoreVariant,
  reloadNonce = 0,
  range: HeartcoreRange = DEFAULT_HEARTCORE_RANGE,
  asReport = false,
) {
  // Seeded from the cache, so a tab whose report is already in memory renders
  // its charts on the very first paint. Switching sub-tabs unmounts and
  // remounts this hook (Radix drops inactive tab content), and starting at
  // `loading` would flash "Loading charts…" every time even though there is
  // nothing left to load.
  const [report, setReport] = useState<QuickLookReport | null>(() =>
    cachedReport(patientId, range),
  );
  const [loading, setLoading] = useState(
    () => cachedReport(patientId, range) === null,
  );
  // Separate from `report === null` so "backend failed" is distinguishable
  // from "patient has no measurements".
  const [error, setError] = useState(false);
  // A failed load already evicted its cache entry, so bumping this resubmits.
  const [retryTick, setRetryTick] = useState(0);

  // Not keyed on `variant`: all three sub-tabs read the same report and only
  // pick different sections out of it, so switching them must never refetch —
  // or re-enter the loading state.
  useEffect(() => {
    let cancelled = false;

    const alreadyLoaded = cachedReport(patientId, range);
    if (alreadyLoaded) {
      setReport(alreadyLoaded);
      setLoading(false);
      setError(false);
      return;
    }

    setLoading(true);
    setReport(null);
    setError(false);

    loadMasterReport(patientId, range)
      .then((data) => {
        if (cancelled) return;
        setReport(data);
      })
      .catch((e) => {
        if (!cancelled) {
          console.error("Failed to load chart report", e);
          setReport(null);
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [patientId, range, reloadNonce, retryTick]);

  const section = (key: QuickLookSection) =>
    asReport && !REPORT_SECTIONS[variant].includes(key)
      ? null
      : findSectionPlot(report, key);

  return {
    retry: () => setRetryTick((t) => t + 1),
    driLvfpPlot: findSectionPlot(report, PRIMARY_SECTION[variant]),
    // The raw + rolling LVFP chart under DRI, on the DRI report only.
    lvfpPlot: asReport ? section(QuickLookSection.lvfp) : null,
    hrPlot: section(QuickLookSection.heart_rate),
    // Its own section in the master report. Older payloads instead hang the
    // probability off the Heart rate plot's `afib_probability`, which the AFib
    // chart falls back to (see afibSourceSeries). Present only when the caller
    // holds the AFIB bit — the BE filters sections by permission.
    afibPlot: section(QuickLookSection.afib),
    bpPlot: section(QuickLookSection.blood_pressure),
    symptomsPlot: section(QuickLookSection.symptoms),
    weightPlot: section(QuickLookSection.weight),
    spo2Plot: section(QuickLookSection.spo2),
    // No eventsPlot: the report JSON carries no events; the charts get them
    // from the patient events endpoint instead (see HeartCoreCharts props).
    metricLabel: VARIANT_LABEL[variant],
    loading,
    error,
  };
}
